/**
 * @fileoverview Pantalla 6 — Resumen final.
 * Muestra conteo de votos y permite cerrar la sesión definitivamente.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LoadingSpinner } from "../components/LoadingSpinner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { useParticipacion } from "../context/ParticipacionContext";
import { useRequireSession } from "../hooks/useSession";
import { completarParticipacion } from "../lib/api";
import { mensajeDeError } from "../lib/utils-app";

export function ResumenFinal() {
    useRequireSession();
    const navigate = useNavigate();
    const { state, statementsPlanas } = useParticipacion();

    const conteo = useMemo(() => {
        let acuerdo = 0;
        let desacuerdo = 0;
        let pasar = 0;
        for (const v of Object.values(state.votos)) {
            if (v === "acuerdo") acuerdo++;
            else if (v === "desacuerdo") desacuerdo++;
            else if (v === "pasar") pasar++;
        }
        return { acuerdo, desacuerdo, pasar, total: acuerdo + desacuerdo + pasar };
    }, [state.votos]);

    const [confirmando, setConfirmando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState(/** @type {string | null} */ (null));

    function handleRegresar() {
        // Volver a la última statement votada (o la actual)
        navigate("/votacion");
    }

    async function handleTerminar() {
        // Evita doble envío por doble clic / múltiples pulsaciones.
        if (enviando) return;
        setEnviando(true);
        setError(null);
        try {
            await completarParticipacion();
            // NO limpiamos aquí: si borramos session_token antes de navegar,
            // el guard useRequireSession redirige a "/" y el participante
            // nunca ve la pantalla de agradecimiento. La limpieza completa
            // ocurre en /confirmacion.
            navigate("/confirmacion", { replace: true });
        } catch (e) {
            setError(mensajeDeError(e));
            setEnviando(false);
            setConfirmando(false);
        }
    }

    return (
        <AppShell testid="page-resumen">
            <div className="space-y-6">
                <div className="space-y-2">
                    <h1 className="font-serif-headline text-3xl sm:text-4xl font-semibold text-foreground">
                        Ya casi terminas
                    </h1>
                    <p className="text-base text-muted-foreground leading-relaxed">
                        Llevas <strong className="text-foreground">{conteo.total}</strong> de{" "}
                        <strong>{statementsPlanas.length}</strong> afirmaciones contestadas. Si quieres,
                        puedes regresar a revisar o cambiar alguna respuesta antes de enviar.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-3" data-testid="conteo-votos">
                    <div className="bg-vote-acuerdo-soft border border-vote-acuerdo/20 rounded-xl p-4 text-center">
                        <p
                            className="text-2xl sm:text-3xl font-semibold text-vote-acuerdo"
                            data-testid="conteo-acuerdo"
                        >
                            {conteo.acuerdo}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                            De acuerdo
                        </p>
                    </div>
                    <div className="bg-vote-desacuerdo-soft border border-vote-desacuerdo/20 rounded-xl p-4 text-center">
                        <p
                            className="text-2xl sm:text-3xl font-semibold text-vote-desacuerdo"
                            data-testid="conteo-desacuerdo"
                        >
                            {conteo.desacuerdo}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                            En desacuerdo
                        </p>
                    </div>
                    <div className="bg-vote-pasar-soft border border-vote-pasar/20 rounded-xl p-4 text-center">
                        <p
                            className="text-2xl sm:text-3xl font-semibold text-vote-pasar"
                            data-testid="conteo-pasar"
                        >
                            {conteo.pasar}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-1">Pasaste</p>
                    </div>
                </div>

                {error ? (
                    <p
                        role="alert"
                        className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                        data-testid="resumen-error"
                    >
                        {error}
                    </p>
                ) : null}

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                        variant="outline"
                        size="lg"
                        onClick={handleRegresar}
                        disabled={enviando}
                        className="sm:flex-1 h-12"
                        data-testid="regresar-revisar-btn"
                    >
                        Regresar a revisar
                    </Button>
                    <Button
                        size="lg"
                        onClick={() => setConfirmando(true)}
                        disabled={enviando}
                        className="sm:flex-1 h-12 font-semibold"
                        data-testid="terminar-enviar-btn"
                    >
                        Terminar y enviar
                    </Button>
                </div>
            </div>

            <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
                <AlertDialogContent data-testid="confirmar-terminar-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Una vez que envíes, no podrás cambiar tus respuestas. Quedarán
                            registradas para el análisis del foro.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={enviando} data-testid="cancelar-terminar-btn">
                            Mejor regreso
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleTerminar}
                            disabled={enviando}
                            data-testid="confirmar-terminar-btn"
                        >
                            {enviando ? (
                                <>
                                    <LoadingSpinner size={16} className="mr-2" /> Enviando...
                                </>
                            ) : (
                                "Sí, enviar"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppShell>
    );
}
